import React from 'react'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as yup from 'yup'
import zxcvbn from 'zxcvbn'
import { Button, Link, InputField, Checkbox } from 'components'
import classes from './RegisterForm.module.scss'
import { NewUser } from 'api/types'
import { useTranslation } from 'react-i18next'

const initialValues: NewUser = {
  email: '',
  password: '',
  confirmPassword: '',
  dataSecurity: false,
}

const validationSchema = yup.object({
  email: yup
    .string()
    .email('pages.login.global.email.invalid')
    .required('pages.login.global.email.required'),
  password: yup
    .string()
    .min(8, 'pages.login.global.password.minComplexity')
    .required('pages.login.global.password.required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'pages.login.global.password.mustMatch')
    .required('pages.login.global.password.repeatRequired'),
  dataSecurity: yup
    .bool()
    .oneOf(
      [true],
      'pages.login.registerPage.registerForm.dataSecurity.required'
    ),
})

const validatePassword = (value: string) => {
  const passwordValidation = zxcvbn(value)
  if (passwordValidation.score < 2) {
    return 'pages.login.global.password.toWeak'
  }
}

interface RegisterFormProps {
  onSubmit: (values: NewUser, formikHelpers: FormikHelpers<NewUser>) => void
}

export const RegisterForm: React.FunctionComponent<RegisterFormProps> = ({
  onSubmit,
}) => {
  const { t } = useTranslation()
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <Field
          component={InputField}
          id="email"
          name="email"
          type="text"
          label={t('global.emailAddress')}
        />

        <div>
          <Field
            component={InputField}
            id="password"
            name="password"
            type="password"
            label={t('global.password')}
            hint={t('pages.login.global.password.minComplexity')}
            validate={validatePassword}
          />

          <Field
            component={InputField}
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            label={t('pages.login.global.password.confirmPassword')}
          />
        </div>

        <Field
          component={Checkbox}
          type="checkbox"
          id="dataSecurity"
          name="dataSecurity"
          className={classes.checkbox}
          label={
            <>
              {t('pages.login.registerPage.registerForm.dataSecurity.iHave')}{' '}
              <Link
                variant="inline"
                to={
                  process.env.PUBLIC_URL +
                  '/Datenschutzhinweise_MBAM_2025_04.pdf'
                }
                target="_blank"
              >
                {t('pages.login.registerPage.registerForm.dataSecurity.policy')}
              </Link>{' '}
              {t(
                'pages.login.registerPage.registerForm.dataSecurity.completeRead'
              )}
            </>
          }
        />

        <Button type="submit" aria-label="Submit">
          {t('global.register')}
        </Button>
      </Form>
    </Formik>
  )
}
